import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Save, Shield, Wallet, Banknote, CalendarClock, X, Plus } from 'lucide-react';
import { configApi, LimitConfig } from '../../api/config.api';
import { useToast } from '../../hooks/use-toast';

const formatAmount = (value: number | null) => {
    if (value === null) return '∞';
    return new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG', minimumFractionDigits: 0 }).format(value);
};

// Status config: color, icon, label mapping
const getStatusConfig = (status: string) => {
    switch (status) {
        case 'unverified':
            return { color: 'red', bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700', badge: 'bg-red-100 text-red-700' };
        case 'pending':
            return { color: 'yellow', bg: 'bg-yellow-50', border: 'border-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' };
        case 'verified':
            return { color: 'green', bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-700', badge: 'bg-green-100 text-green-700' };
        case 'vip':
            return { color: 'purple', bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' };
        default:
            return { color: 'slate', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-700' };
    }
};

export function LimitsConfig() {
    const { toast } = useToast();
    const [limits, setLimits] = useState<LimitConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<LimitConfig>>({});
    const [saving, setSaving] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newLimit, setNewLimit] = useState<Partial<LimitConfig>>({ kyc_status: '', is_active: true });
    const [creating, setCreating] = useState(false);

    const loadLimits = useCallback(async () => {
        try {
            setLoading(true);
            const response = await configApi.getLimits();
            const data = response as any;
            const limitsArray = Array.isArray(data) ? data : (data?.results || []);
            setLimits(limitsArray);
        } catch (err) {
            console.error('Error loading limits:', err);
            toast({ title: 'Erè', description: 'Pa kapab chaje limit yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadLimits();
    }, [loadLimits]);

    const handleEdit = (limit: LimitConfig) => {
        setEditingId(limit.id);
        setEditForm({ ...limit });
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSave = async () => {
        if (!editingId) return;

        try {
            setSaving(true);
            await configApi.updateLimit(editForm.kyc_status!, editForm);

            setLimits(prev => prev.map(l =>
                l.id === editingId ? { ...l, ...editForm } as LimitConfig : l
            ));
            setEditingId(null);
            setEditForm({});
            toast({ title: 'Siksè', description: 'Limit yo aktyalize', type: 'success' });
        } catch (err) {
            console.error('Error saving limits:', err);
            toast({ title: 'Erè', description: 'Pa kapab sovgade limit yo', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreate = async () => {
        if (!newLimit.kyc_status) {
            toast({ title: 'Erè', description: 'Chwazi yon estati KYC', type: 'error' });
            return;
        }

        try {
            setCreating(true);
            const created = await configApi.createLimit(newLimit);
            setLimits(prev => [...prev, created]);
            setNewLimit({ kyc_status: '', is_active: true });
            setShowAddForm(false);
            toast({ title: 'Siksè', description: 'Limit kreye avèk siksè', type: 'success' });
        } catch (err: any) {
            console.error('Error creating limit:', err);
            toast({ title: 'Erè', description: 'Pa kapab kreye limit la', type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleInputChange = (field: keyof LimitConfig, value: string) => {
        const numValue = value === '' ? null : Number(value);
        setEditForm(prev => ({ ...prev, [field]: numValue }));
    };

    const handleNewLimitChange = (field: keyof LimitConfig, value: string) => {
        const numValue = value === '' ? null : Number(value);
        setNewLimit(prev => ({ ...prev, [field]: numValue }));
    };

    // Filter out existing statuses from options
    const KYC_STATUS_OPTIONS = [
        { value: 'unverified', label: 'Non Vérifié' },
        { value: 'pending', label: 'En Attente' },
        { value: 'verified', label: 'Vérifié' },
        { value: 'basique', label: 'Basique' },
        { value: 'intermediaire', label: 'Intermédiaire' },
        { value: 'avance', label: 'Avancé' },
        { value: 'vip', label: 'VIP' },
    ];

    const availableStatuses = KYC_STATUS_OPTIONS.filter(
        opt => !limits.some(l => l.kyc_status === opt.value)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-3 text-slate-600">Chajman limit KYC...</span>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6">
            {/* Header info */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary-100 rounded-lg">
                            <Shield className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Limit pa Estati KYC</h3>
                            <p className="text-sm text-slate-600 mt-1">
                                Defini limit tranzaksyon selon estati verifikasyon kliyan an.
                            </p>
                        </div>
                    </div>
                    {availableStatuses.length > 0 && (
                        <Button onClick={() => setShowAddForm(!showAddForm)} size="sm" className="gap-2">
                             {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                             {showAddForm ? 'Fèmen' : 'Ajoute Limite'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Creation Form */}
            {showAddForm && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200 shadow-sm animate-in fade-in slide-in-from-top-4">
                     <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-600" />
                        Ajoute Nouvo Limite
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Estati KYC</label>
                            <select
                                className="w-full h-9 rounded-md border border-slate-300 text-sm px-3 bg-white"
                                value={newLimit.kyc_status || ''}
                                onChange={(e) => setNewLimit(prev => ({ ...prev, kyc_status: e.target.value }))}
                            >
                                <option value="">Chwazi estati...</option>
                                {availableStatuses.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Balans Max</label>
                            <Input
                                type="number"
                                placeholder="Illimité"
                                className="h-9 bg-white"
                                value={newLimit.max_balance ?? ''}
                                onChange={(e) => handleNewLimitChange('max_balance', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max pa Transaksyon</label>
                            <Input
                                type="number"
                                placeholder="Illimité"
                                className="h-9 bg-white"
                                value={newLimit.per_transaction_max ?? ''}
                                onChange={(e) => handleNewLimitChange('per_transaction_max', e.target.value)}
                            />
                        </div>
                        <div className="pt-6">
                            <Button onClick={handleCreate} disabled={creating || !newLimit.kyc_status} className="w-full gap-2">
                                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Kreye Limite
                            </Button>
                        </div>
                     </div>
                     <div className="mt-4 pt-4 border-t border-blue-200/60 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max par Jour (Nombre)</label>
                            <Input
                                type="number"
                                placeholder="Illimité"
                                className="h-9 bg-white"
                                value={newLimit.daily_transaction_count ?? ''}
                                onChange={(e) => handleNewLimitChange('daily_transaction_count', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max par Jour (Montant)</label>
                            <Input
                                type="number"
                                placeholder="Illimité"
                                className="h-9 bg-white"
                                value={newLimit.daily_amount ?? ''}
                                onChange={(e) => handleNewLimitChange('daily_amount', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Max par Mois (Montant)</label>
                            <Input
                                type="number"
                                placeholder="Illimité"
                                className="h-9 bg-white"
                                value={newLimit.monthly_amount ?? ''}
                                onChange={(e) => handleNewLimitChange('monthly_amount', e.target.value)}
                            />
                        </div>
                     </div>
                </div>
            )}

            {/* Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {limits.map((limit) => {
                    const isEditing = editingId === limit.id;
                    const style = getStatusConfig(limit.kyc_status);

                    return (
                        <div
                            key={limit.id}
                            className={`rounded-xl border transition-all duration-200 overflow-hidden ${isEditing
                                ? 'border-primary-500 ring-1 ring-primary-500 shadow-lg bg-white z-10 scale-[1.02]'
                                : 'border-slate-200 hover:border-slate-300 hover:shadow-sm bg-white'
                                }`}
                        >
                            {/* Card Header with Status Color */}
                            <div className={`px-4 py-3 border-b ${style.border} ${style.bg} flex items-center justify-between`}>
                                <div className="flex items-center gap-2">
                                    <Shield className={`h-4 w-4 ${style.text}`} />
                                    <span className={`font-semibold text-sm ${style.text}`}>
                                        {limit.kyc_status_display}
                                    </span>
                                </div>
                                {!isEditing && (
                                    <button
                                        onClick={() => handleEdit(limit)}
                                        className="text-slate-400 hover:text-primary-600 transition-colors text-xs font-medium px-2 py-1 rounded hover:bg-white/50"
                                    >
                                        Modifye
                                    </button>
                                )}
                            </div>

                            <div className="p-4 space-y-4">
                                {/* Balance Section */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Wallet className="h-3.5 w-3.5" />
                                        <span>Limit Balans</span>
                                    </div>
                                    <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                className="h-8 text-sm bg-white"
                                                placeholder="∞"
                                                value={editForm.max_balance ?? ''}
                                                onChange={(e) => handleInputChange('max_balance', e.target.value)}
                                            />
                                        ) : (
                                            <p className="font-semibold text-slate-900 text-lg">
                                                {formatAmount(limit.max_balance)}
                                            </p>
                                        )}
                                        <p className="text-[10px] text-slate-400 mt-1">Balans maksimòm otorize</p>
                                    </div>
                                </div>

                                {/* Transactions Section */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Banknote className="h-3.5 w-3.5" />
                                        <span>Limit Tranzaksyon</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Per Transaction */}
                                        <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                            <p className="text-[10px] text-slate-500 mb-1">Pa tranzaksyon</p>
                                            {isEditing ? (
                                                <Input
                                                    type="number"
                                                    className="h-7 text-xs bg-white px-2"
                                                    placeholder="∞"
                                                    value={editForm.per_transaction_max ?? ''}
                                                    onChange={(e) => handleInputChange('per_transaction_max', e.target.value)}
                                                />
                                            ) : (
                                                <p className="font-medium text-slate-900 text-sm">
                                                    {formatAmount(limit.per_transaction_max)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Daily Count */}
                                        <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                            <p className="text-[10px] text-slate-500 mb-1">Kantite / jou</p>
                                            {isEditing ? (
                                                <Input
                                                    type="number"
                                                    className="h-7 text-xs bg-white px-2"
                                                    placeholder="∞"
                                                    value={editForm.daily_transaction_count ?? ''}
                                                    onChange={(e) => handleInputChange('daily_transaction_count', e.target.value)}
                                                />
                                            ) : (
                                                <p className="font-medium text-slate-900 text-sm">
                                                    {limit.daily_transaction_count ?? '∞'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Monthly Limits */}
                                <div className="space-y-3 pt-2 border-t border-slate-100">
                                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <CalendarClock className="h-3.5 w-3.5" />
                                        <span>Plafond Peryodik</span>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600 text-xs">Jounalye:</span>
                                            {isEditing ? (
                                                <Input
                                                    type="number"
                                                    className="h-7 w-28 text-xs text-right"
                                                    placeholder="∞"
                                                    value={editForm.daily_amount ?? ''}
                                                    onChange={(e) => handleInputChange('daily_amount', e.target.value)}
                                                />
                                            ) : (
                                                <span className="font-medium text-slate-900">{formatAmount(limit.daily_amount)}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600 text-xs">Mansyèl:</span>
                                            {isEditing ? (
                                                <Input
                                                    type="number"
                                                    className="h-7 w-28 text-xs text-right"
                                                    placeholder="∞"
                                                    value={editForm.monthly_amount ?? ''}
                                                    onChange={(e) => handleInputChange('monthly_amount', e.target.value)}
                                                />
                                            ) : (
                                                <span className="font-medium text-slate-900">{formatAmount(limit.monthly_amount)}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                {isEditing && (
                                    <div className="pt-2 flex gap-2">
                                        <Button
                                            size="sm"
                                            className="flex-1 text-xs h-8"
                                            onClick={handleSave}
                                            disabled={saving}
                                        >
                                            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                                            Sovgade
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="px-3 h-8"
                                            onClick={handleCancel}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
